import React from 'react';
import { InterPoint } from '@saviynt/common';
import {
  Box,
  Button,
  Chip,
  Icon,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { format, isValid } from 'date-fns';

import { SodApplicationGroup } from '../../../../../shared/components';
import {
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_BLD,
  CF_SEC_MED,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import {
  type ViolatingEntitlements,
  type ViolationAssignedEntitlements,
  type ViolationFunctions,
} from '../../../data-layer/detailsTypes';

interface ConflictLeadingContentProps {
  level: number;
  isObject?: boolean;
}

/**
 * Renders different leading content based on the level and provided data.
 *
 * @param {Object} props - Component props
 * @param {number} props.level - The level of the conflict row (0 for function level, 1 for entitlement level)
 * @param {boolean} props.isObject - Weather to populate the object or entitlemnt
 * @returns {React.ReactElement} The rendered conflict leading content
 */
function ConflictLeadingContent({
  level,
  isObject,
}: ConflictLeadingContentProps) {
  if (level === 0) {
    return <Icon kind='JobFunction' color={CF_SEC_MED} />;
  }

  if (level >= 2 && isObject) {
    return <Icon kind='Object' color={CF_SEC_MED} />;
  }

  return <Icon kind='Entitlement' color={CF_SEC_MED} />;
}

ConflictLeadingContent.defaultProps = {
  isObject: false,
};

interface ConflictObjectProps {
  fieldName: string;
  userValue: string;
  ruleValue: string;
  showObjectFieldLabel: boolean;
  objectValue: string;
}

interface ConflictCommonProps {
  level: number;
  fnRow?: ViolationFunctions;
  entRow?: ViolationAssignedEntitlements;
  vioRow?: ViolatingEntitlements;
  vioObj?: ConflictObjectProps;
  isObject?: boolean;
}

const getCleanedValue = (value: string | null | undefined) =>
  value && value.trim().length > 0 ? value : '-';

/**
 * Renders different center content based on the level and provided data.
 *
 * @param {Object} props - Component props
 * @param {number} props.level - The level of the conflict row (0 for function level, 1 for entitlement level)
 * @param {ViolationFunctions} [props.fnRow] - Function row data, required when level is 0
 * @param {ViolationAssignedEntitlements} [props.entRow] - Entitlement row data, required when level is 1
 * @param {boolean} props.isObject - Weather to populate the object or entitlemnt
 * @returns {React.ReactElement} The rendered conflict center content
 */
function ConflictCenterContent({
  level,
  fnRow,
  entRow,
  vioRow,
  vioObj,
  isObject,
}: ConflictCommonProps) {
  const l = useInvertedLocalize();
  const {
    aag: {
      global: { globalDateFormat },
    },
  } = useTypedSelector((state) => state);

  const centerContentClasses = cx(
    'Sod-ViolationsTabDetailsConflict-centerContent',
    `Sod-ViolationsTabDetailsConflict-centerContent--isLevel-${level}`
  );

  if (level === 0 && fnRow) {
    return (
      <Box tag='div' className={centerContentClasses}>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentHeadingWrapper'>
          <Typography kind='body3' color={CF_SEC_MED}>
            {l('Function')}
          </Typography>
          <Typography kind='body1-bold' lineLimit={1} color={CF_NEU_BLD}>
            {fnRow.name}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentBodyWrapper'>
          <SodApplicationGroup
            label={fnRow.endpointName}
            firstLogo={fnRow.endpointLogo ?? undefined}
          />
          <InterPoint color='neutralBold' font='body2' />
          <Typography kind='body2' color={CF_NEU_STL}>
            {l('Account:')}
          </Typography>
          <Typography kind='body2' color={CF_NEU_BLD} isNoWrap>
            {fnRow.accountName}
          </Typography>
          {fnRow.description && (
            <>
              <InterPoint color='neutralBold' font='body2' />
              <Typography
                kind='body2'
                color={CF_NEU_STL}
                lineLimit={1}
                isNoWrap>
                {fnRow.description}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (level === 1 && entRow) {
    return (
      <Box tag='div' className={centerContentClasses}>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentHeadingWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_SEC_MED}>
            {l('Assigned Entitlement')}
          </Typography>
          {entRow.entitlementTypeName &&
            entRow.entitlementTypeName.trim().length > 0 && (
              <>
                <InterPoint color='neutralBold' font='body2' />
                <Typography
                  kind='body3'
                  desktop='body3-d'
                  mobile='body3-d'
                  color={CF_SEC_MED}>
                  {entRow.entitlementTypeName}
                </Typography>
              </>
            )}
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentBodyWrapper'>
          <Typography
            kind='h3'
            desktop='h3-d'
            mobile='h3-d'
            color={CF_NEU_BLD}
            lineLimit={1}>
            {entRow.entitlementDisplayName}
          </Typography>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_SEC_MED}
            lineLimit={1}>
            {entRow.entitlementValue}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (level === 2 && vioRow && !isObject) {
    return (
      <Box tag='div' className={centerContentClasses}>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Box className='Sod-ViolationsTabDetailsConflict-centerContentTitleField'>
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color={CF_SEC_MED}>
              {l('Violating Entitlement')}
            </Typography>
            {vioRow.entitlementTypeName &&
              vioRow.entitlementTypeName.trim().length > 0 && (
                <>
                  <InterPoint color='neutralBold' font='body2' />
                  <Typography
                    kind='body3'
                    desktop='body3-d'
                    mobile='body3-d'
                    color={CF_SEC_MED}>
                    {vioRow.entitlementTypeName}
                  </Typography>
                </>
              )}
          </Box>
          <Typography
            kind='h3'
            desktop='h3-d'
            mobile='h3-d'
            color={CF_NEU_BLD}
            lineLimit={1}
            className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapperValue'>
            {vioRow.entitlementDisplayName}
          </Typography>
        </Box>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_NEU_STL}>
            {l('First Used')}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={CF_SEC_BLD}>
            {vioRow.firstUsed &&
            vioRow.firstUsed.trim().length > 0 &&
            isValid(new Date(vioRow.firstUsed))
              ? format(new Date(vioRow.firstUsed), globalDateFormat)
              : '-'}
          </Typography>
        </Box>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_NEU_STL}>
            {l('Last Used')}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={CF_SEC_BLD}>
            {vioRow.lastUsed &&
            vioRow.lastUsed.trim().length > 0 &&
            isValid(new Date(vioRow.lastUsed))
              ? format(new Date(vioRow.lastUsed), globalDateFormat)
              : '-'}
          </Typography>
        </Box>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_NEU_STL}>
            {l('Total Used')}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={CF_SEC_BLD}>
            {vioRow.totalUsed !== null ? vioRow.totalUsed : '-'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (level >= 2 && (vioRow || vioObj)) {
    return (
      <Box tag='div' className={centerContentClasses}>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_SEC_MED}>
            {l('Object')}
          </Typography>
          <Typography kind='h3' desktop='h3-d' mobile='h3-d' color={CF_NEU_BLD}>
            {vioObj ? vioObj.objectValue : vioRow?.objectValue}
          </Typography>
        </Box>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_NEU_STL}>
            {l('Field')}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={CF_SEC_BLD}>
            {getCleanedValue(vioObj ? vioObj.fieldName : vioRow?.fieldName)}
          </Typography>
        </Box>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_NEU_STL}>
            {l('User Value')}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={CF_SEC_BLD}>
            {getCleanedValue(vioObj ? vioObj.userValue : vioRow?.userValue)}
          </Typography>
        </Box>
        <Box
          tag='div'
          className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={CF_NEU_STL}>
            {l('Rule Value')}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={CF_SEC_BLD}>
            {getCleanedValue(vioObj ? vioObj.ruleValue : vioRow?.ruleValue)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return null;
}

ConflictCenterContent.defaultProps = {
  fnRow: null,
  entRow: null,
  vioRow: null,
  vioObj: null,
  isObject: false,
};

interface ConflictTrailingContentProps extends ConflictCommonProps {
  handleTrigger?: () => void;
  appearanceCount?: number;
}

/**
 * Renders different trailing content based on the level and provided data.
 *
 * @param {Object} props - Component props
 * @param {number} props.level - The level of the conflict row (0 for function level, 1 for entitlement level)
 * @param {ViolationFunctions} [props.fnRow] - Function row data, required when level is 0
 * @param {ViolationAssignedEntitlements} [props.entRow] - Entitlement row data, required when level is 1
 * @param {function} [props.handleTrigger] - To trigger function row trailing content click
 * @returns {React.ReactElement} The rendered conflict trailing content
 */
function ConflictTrailingContent({
  level,
  fnRow,
  entRow,
  vioRow,
  isObject,
  appearanceCount,
}: Omit<ConflictTrailingContentProps, 'vioObj'>) {
  const l = useInvertedLocalize();
  const trailContentWrapperClasses = cx(
    'Sod-ViolationsTabDetailsConflict-trailingContent',
    `Sod-ViolationsTabDetailsConflict-trailingContent--isLevel-${level}`
  );

  if (level === 0 && fnRow) {
    return (
      <Box tag='div' className={trailContentWrapperClasses}>
        <Typography kind='body3' color={CF_NEU_STL}>
          {l(
            '{count, plural, one {Assigned Entitlement} other {Assigned Entitlements}}',
            { count: fnRow.assignedEntitlementsCount ?? 0 }
          )}
        </Typography>
        <Typography kind='body1-bold' color={CF_SEC_MED}>
          {fnRow.assignedEntitlementsCount ?? 0}
        </Typography>
      </Box>
    );
  }

  if (level === 1 && entRow) {
    const isCritical = Boolean(entRow.entitlementTrustScore <= 50);
    const chipClasses = cx(
      isCritical
        ? 'Sod-ViolationsTabDetailsConflict-trailingContent--isCritical'
        : 'Sod-ViolationsTabDetailsConflict-trailingContent--isSuccess'
    );

    return (
      <Box tag='div' className={trailContentWrapperClasses}>
        {entRow.occurrencesInFunctions > 1 && (
          <Tooltip
            maxWidth='15rem'
            trigger={
              <Chip
                size='xSmall'
                label={l(
                  'Multiple occurrences {currentCount} of {totalCount}',
                  {
                    currentCount:
                      appearanceCount === undefined ? 1 : appearanceCount + 1,
                    totalCount: entRow.occurrencesInFunctions,
                  }
                )}
                kind='boldFilled'
                color='info'
              />
            }
            isPortalEnabled
            text={l(
              'This entitlement appears in more than one job function within the same violation. This indicates this entitlement carries inherent risk.'
            )}
            placement='bottom-end'
          />
        )}
        <Chip
          color={isCritical ? 'critical' : 'success'}
          size='xSmall'
          label={entRow.entitlementTrustScore}
          className={chipClasses}
          leftIcon={<Icon kind={isCritical ? 'Sad' : 'Happy'} />}
        />
      </Box>
    );
  }

  if (isObject) {
    return null;
  }

  if (level === 2 && vioRow) {
    const isCritical = Boolean(vioRow.entitlementTrustScore <= 50);
    const chipClasses = cx(
      isCritical
        ? 'Sod-ViolationsTabDetailsConflict-trailingContent--isCritical'
        : 'Sod-ViolationsTabDetailsConflict-trailingContent--isSuccess'
    );

    return (
      <Box tag='div' className={trailContentWrapperClasses}>
        <Chip
          color={isCritical ? 'critical' : 'success'}
          size='xSmall'
          label={vioRow.entitlementTrustScore}
          className={chipClasses}
          leftIcon={<Icon kind={isCritical ? 'Sad' : 'Happy'} />}
        />
      </Box>
    );
  }

  return null;
}

ConflictTrailingContent.defaultProps = {
  fnRow: null,
  entRow: null,
  vioRow: null,
  isObject: false,
  handleTrigger: () => {},
};

interface ConflictAfterContentProps {
  isDisabled?: boolean;
  isRetry?: boolean;
  onClick?: () => void;
}

/**
 * Renders different after content based on the level and provided data.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isDisabled - If the button is disabled
 * @param {boolean} props.isRetry - If the state should be `retry` or not
 * @param {function} props.onClick - Click handler for the button
 * @returns {React.ReactElement} The rendered conflict trailing content
 */
function ConflictAfterContent({
  isDisabled,
  isRetry,
  onClick,
}: ConflictAfterContentProps) {
  const l = useInvertedLocalize();

  return (
    <Box tag='div' className='Sod-ViolationsTabDetailsConflict-afterContent'>
      <Button
        type='button'
        kind={isRetry ? 'outlined' : 'subtle'}
        isEnabled={isRetry}
        isDisabled={isDisabled}
        onClick={onClick}>
        {l(isRetry ? 'Retry' : 'Show More')}
      </Button>
    </Box>
  );
}

ConflictAfterContent.defaultProps = {
  isDisabled: false,
  isRetry: false,
  onClick: () => {},
};

export {
  ConflictAfterContent,
  ConflictCenterContent,
  ConflictLeadingContent,
  ConflictTrailingContent,
};
